import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { CCard, CCardBody, CButton, CBadge, CFormCheck } from '@coreui/react';
import _ from 'lodash';
import { languageOptions } from './exportData';
import { toast } from 'react-toastify';
import VoicePreviewPanel from './VoicePreviewPanel';
import {
    DEFAULT_VOICE_AI_MODEL,
    VOICE_PREVIEW_MODEL_OPTIONS,
    hasStoredVoiceModel,
} from './aiVoiceModels';
import './Step3.css';

function effectiveStep3ModelId(model) {
    if (!hasStoredVoiceModel(model)) return DEFAULT_VOICE_AI_MODEL;
    return VOICE_PREVIEW_MODEL_OPTIONS.some((o) => o.id === model)
        ? model
        : DEFAULT_VOICE_AI_MODEL;
}

export const Step3 = ({ selectedLanguages, setSelectedLanguages, projectId, saveChangesNavigate, initialLanguages, stepChanges }) => {
    /** Persist Gemini only (no Model 2 / GPT in this flow). */
    useEffect(() => {
        setSelectedLanguages((prev) => {
            const next = prev.map((l) => ({
                ...l,
                model: effectiveStep3ModelId(l.model),
            }));
            const same =
                prev.length === next.length &&
                prev.every((l, i) => l.value === next[i].value && l.model === next[i].model);
            return same ? prev : next;
        });
    }, [selectedLanguages, setSelectedLanguages]);

    const handleClickNext = () => {
        if (selectedLanguages.length === 0) {
            toast.error('At least one language needs to be selected to proceed further.');
            return;
        }
        saveChangesNavigate();
    }

    const handleClickPrev = () => {
        stepChanges(3, 1);
    };

    const handleCheckboxChange = (event) => {
        const { value, checked } = event.target;
        const selected = [...selectedLanguages];

        if (checked) {
            // Check if user is trying to select more than 8 languages
            if (selected.length >= 8) {
                toast.error("Maximum language limit reached. Please deselect a language before adding a new one.");
                return;
            }
            selected.push({
                value,
                label: event.target.name,
                model: DEFAULT_VOICE_AI_MODEL,
            });
        } else {
            // Remove the unselected language
            _.remove(selected, lang => lang.value === value);
        }

        setSelectedLanguages(selected);
    };

    const handleLanguageModelChange = (langValue, modelId) => {
        setSelectedLanguages((prev) =>
            prev.map((lang) => (lang.value === langValue ? { ...lang, model: modelId } : lang))
        );
    };

    return (
        <div>
            <CCard style={{ marginTop: '1rem', marginBottom: '1rem' }} className='ccard'>
                <CCardBody>
                    <h5 style={{color:'#fcaf17'}}>Select the languages required for this project</h5>
                    <p className="step3-matrix-intro">
                        Choose which languages are in scope. Each language uses <strong>Gemini</strong> (Model 1).
                    </p>
                    <div style={{ marginBottom: '1rem', color: selectedLanguages.length >= 8 ? '#ff6b6b' : '#fcaf17', fontSize: '0.9rem' }}>
                        Selected: {selectedLanguages.length}/8 languages
                        {selectedLanguages.length >= 8 && <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>(Maximum reached)</span>}
                    </div>
                    <div className="selected-languages-chips">
                        {selectedLanguages.map((lang) => (
                            <CBadge key={lang.value} color="primary" style={{ margin: '0.25rem' }}>
                                {lang.label}
                                <span style={{ opacity: 0.85, fontSize: '0.75rem', marginLeft: '0.35rem' }}>
                                    (
                                    {VOICE_PREVIEW_MODEL_OPTIONS.find(
                                        (m) => m.id === effectiveStep3ModelId(lang.model)
                                    )?.label || effectiveStep3ModelId(lang.model)}
                                    )
                                </span>
                            </CBadge>
                        ))}
                    </div>

                    <div className="step3-table-wrap">
                        <table className="step3-lang-table">
                            <thead>
                                <tr>
                                    <th scope="col" className="step3-lang-table__col-lang">Language</th>
                                    <th scope="col" className="step3-lang-table__col-use">Use</th>
                                    <th scope="col" className="step3-lang-table__col-model">
                                        AI model
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {languageOptions.map((option) => {
                                    const isChecked = selectedLanguages.some((lang) => lang.value === option.value);
                                    const entry = selectedLanguages.find((lang) => lang.value === option.value);
                                    const modelValue =
                                        isChecked && entry
                                            ? effectiveStep3ModelId(entry.model)
                                            : null;
                                    return (
                                        <tr
                                            key={option.value}
                                            className={isChecked ? 'step3-lang-table__row--on' : 'step3-lang-table__row--off'}
                                        >
                                            <td className="step3-lang-table__cell-lang">
                                                <span className="step3-lang-table__lang-name">{option.label}</span>
                                            </td>
                                            <td className="step3-lang-table__cell-use">
                                                <CFormCheck
                                                    id={`step3-lang-${option.value}`}
                                                    name={option.label}
                                                    value={option.value}
                                                    checked={isChecked}
                                                    onChange={handleCheckboxChange}
                                                    label=" "
                                                    aria-label={`Include ${option.label} in this project`}
                                                />
                                            </td>
                                            <td className="step3-lang-table__cell-model">
                                                {isChecked ? (
                                                    <div
                                                        className="step3-model-pills"
                                                        role="radiogroup"
                                                        aria-label={`Model for ${option.label}`}
                                                    >
                                                        {VOICE_PREVIEW_MODEL_OPTIONS.map((opt) => {
                                                            const pillText = opt.uiLabel || opt.pillLabel || opt.label;
                                                            const active = modelValue === opt.id;
                                                            return (
                                                                <label
                                                                    key={opt.id}
                                                                    className={`step3-model-pill ${active ? 'step3-model-pill--active' : ''}`}
                                                                    title={opt.label}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name={`step3-model-${option.value}`}
                                                                        value={opt.id}
                                                                        checked={active}
                                                                        onChange={() =>
                                                                            handleLanguageModelChange(option.value, opt.id)
                                                                        }
                                                                    />
                                                                    <span>{pillText}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span className="step3-lang-table__model-placeholder">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                </CCardBody>
            </CCard>

            <VoicePreviewPanel />

            <div style={{ marginTop: '1rem',marginBlockStart:'14px', display: 'flex', gap: '1rem' }}>
                <CButton color="secondary" onClick={handleClickPrev}>Previous</CButton>
                <CButton color="primary" onClick={handleClickNext} disabled={selectedLanguages.length === 0}
                >Save</CButton>
            </div>
        </div>
    );
};

Step3.propTypes = {
    prevStep: PropTypes.func.isRequired,
    selectedLanguages: PropTypes.array.isRequired,
    setSelectedLanguages: PropTypes.func.isRequired,
    projectId: PropTypes.string.isRequired,
    initialLanguages: PropTypes.array.isRequired,
    saveChangesNavigate: PropTypes.func.isRequired,
};
